import { z } from "zod";

// All PUBLIC_* configuration is injected at container startup: docker-entrypoint.sh
// substitutes the placeholders in index.html into window.__RUNTIME_CONFIG__ (and
// webpack-dev-server does the same locally). Nothing is baked into the JS bundle
// at build time, so a single image can be deployed to ANY environment by varying
// the env vars passed to the container — adding an environment is a deploy-time
// concern, no rebuild required.
//
// IMPORTANT: never branch business logic on the PUBLIC_ENVIRONMENT label. It is
// for analytics / observability / display only. Use the boolean capability flags
// (PUBLIC_IS_PRODUCTION, PUBLIC_THIRD_PARTY_TOOLS_ENABLED) so behaviour is driven
// by configuration rather than hard-coded environment names.
const booleanFromEnv = z
    .union([z.boolean(), z.string()])
    .transform(value =>
        typeof value === "boolean" ? value : value === "true",
    );

export const runtimeConfigurationSchema = z.object({
    // Free-form label for the current environment (e.g. "development",
    // "staging", "us-staging", "production"). Display / analytics only.
    PUBLIC_ENVIRONMENT: z.string().min(1, "Environment label is required"),

    // True only for production environments. Drives any gate that previously
    // checked the environment name for "production".
    PUBLIC_IS_PRODUCTION: booleanFromEnv,

    // Whether third-party tools (Datadog RUM, Userpilot, Zendesk, in-app help)
    // should be initialised. Enabled in deployed environments, disabled for
    // local development and automated test runs.
    PUBLIC_THIRD_PARTY_TOOLS_ENABLED: booleanFromEnv,

    // Odin backend the SPA talks to (HTTP API + websocket).
    PUBLIC_ODIN_API_URL: z.string().min(1, "Odin API URL is required"),
    PUBLIC_ODIN_WS_URL: z.string().min(1, "Odin websocket URL is required"),

    // PRODA / rentroll app, Core UI app and the rentroll page the SPA links to.
    PUBLIC_PRODA_URL: z.string().min(1, "PRODA URL is required"),
    PUBLIC_CORE_URL: z.string().min(1, "Core UI URL is required"),
    PUBLIC_RENTROLL_PAGE_URL: z
        .string()
        .min(1, "Rentroll page URL is required"),

    // Third-party integration credentials.
    PUBLIC_USERPILOT_TOKEN: z.string().min(1, "Userpilot token is required"),
    PUBLIC_ZENDESK_KEY: z.string().min(1, "Zendesk key is required"),
    PUBLIC_AG_GRID_LICENSE_KEY: z
        .string()
        .min(1, "AG Grid license key is required"),
    PUBLIC_DATADOG_RUM_APPLICATION_ID: z
        .string()
        .min(1, "Datadog RUM application ID is required"),
    PUBLIC_DATADOG_RUM_CLIENT_TOKEN: z
        .string()
        .min(1, "Datadog RUM client token is required"),

    // Optional feature flags.
    PUBLIC_DISABLE_GRID_VIRTUALIZATION: z.string().optional(),
});

export const environmentConfigurationSchema = runtimeConfigurationSchema;

export type RuntimeConfig = z.infer<typeof runtimeConfigurationSchema>;
export type EnvironmentConfig = z.infer<typeof environmentConfigurationSchema>;
